/**
 * Bundle Builder JavaScript
 * Handles product selection, pricing calculations, and cart integration
 */

// Debug helper functions
const DEBUG_MODE = true; // Set to false to disable debug logging

function debugLog(...args) {
  if (DEBUG_MODE && console && console.log) {
    console.log('[Bundle Builder]', ...args);
  }
}

function debugWarn(...args) {
  if (DEBUG_MODE && console && console.warn) {
    console.warn('[Bundle Builder]', ...args);
  }
}

function debugError(...args) {
  if (console && console.error) {
    console.error('[Bundle Builder]', ...args);
  }
}

class BundleBuilder {
  constructor(options = {}) {
    this.pageId = options.pageId || 'default';
    this.pageHandle = options.pageHandle || 'bundle-builder';
    this.discountPercentage = options.discountPercentage || 15;
    this.discountCode = options.discountCode || `BUNDLE${options.discountPercentage || 15}`;
    this.minProducts = options.minProducts || 2;
    this.maxProducts = options.maxProducts || 5;
    this.moneyFormat = options.moneyFormat || '{{amount_with_comma_separator}} €';
    this.translations = options.translations || this.getDefaultTranslations();
    
    this.selectedProducts = new Map();
    this.categoryLimits = new Map();
    this.activeLine = null;
    this.lineButtons = [];
    this.categoryElements = [];
    this.categoryMeta = new Map();
    this.categorySteps = ['sets', 'cars', 'accessories'];
    
    // Create unique storage key for this page
    this.storageKey = `revell-bundle-builder-${this.pageHandle}`;
    
    this.init();
  }
  
  getDefaultTranslations() {
    return {
      select: 'Select',
      deselect: 'Deselect',
      empty: 'Choose your products to create your bundle',
      add_to_cart: 'Add Bundle to Cart',
      adding: 'Adding...',
      remaining: 'Select {{ count }} more product{{ plural }}',
      unavailable: 'This product is {{ status }} and cannot be selected.',
      status_unavailable: 'unavailable',
      status_sold_out: 'sold out',
      category_max: 'Maximum selection reached ({{ max }})',
      products_max: 'Maximum of {{ max }} products reached',
      products_min: 'Please select at least {{ min }} products',
      success: 'Bundle successfully added!',
      error: 'Error adding. Please try again.'
    };
  }
  
  init() {
    this.cacheDom();
    this.setupLineSwitcher();
    this.bindEvents();
    this.setupCategoryLimits();
    this.setupSearch();
    this.setupCategoryAccordions();
    this.updateUI();
    this.loadFromStorage();
  }

  cacheDom() {
    this.lineButtons = Array.from(document.querySelectorAll('.bundle-line-button'));
    this.categoryElements = Array.from(document.querySelectorAll('.bundle-category'));
  }
  
  bindEvents() {
    // Product selection
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('product-checkbox')) {
        this.handleProductSelection(e.target);
      }
    });
    
    // Clear bundle
    const clearButton = document.querySelector('.clear-bundle');
    if (clearButton) {
      clearButton.addEventListener('click', () => this.clearBundle());
    }
    
    // Add to cart
    const addToCartButton = document.querySelector('.add-bundle-to-cart');
    if (addToCartButton) {
      addToCartButton.addEventListener('click', () => this.addBundleToCart());
    }
    
    // Remove individual products
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('remove-product')) {
        const productId = e.target.dataset.productId;
        this.removeProduct(productId);
      }
    });
  }
  
  setupCategoryLimits() {
    const categories = document.querySelectorAll('.bundle-category');
    debugLog(`🏗️ Setup für ${categories.length} Kategorien:`);
    
    categories.forEach((category, index) => {
      const maxSelection = parseInt(category.dataset.maxSelection) || 1;
      const categoryId = parseInt(category.dataset.categoryId);
      const unlockThreshold = parseInt(category.dataset.unlockThreshold) || 1;
      const categoryRole = category.dataset.categoryRole || 'sets';
      const trackLine = category.dataset.trackLine || '';
      
      debugLog(`📋 Kategorie ${categoryId} (Index ${index}): Max = ${maxSelection}`);
      
      // Use categoryId as key instead of index
      this.categoryLimits.set(categoryId, {
        max: maxSelection,
        selected: 0,
        threshold: unlockThreshold
      });
      
      this.categoryMeta.set(categoryId, {
        role: categoryRole,
        line: trackLine,
        element: category
      });
    });
    
    debugLog('🗂️ Alle Kategorie-Limits:', this.categoryLimits);
    this.evaluateStepProgress();
  }

  setupLineSwitcher() {
    if (!this.lineButtons.length) return;

    const preselected = this.lineButtons.find(button => button.classList.contains('is-active'));
    this.activeLine = preselected ? preselected.dataset.line : this.lineButtons[0].dataset.line;
    this.applyLineFilter(this.activeLine);

    this.lineButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const selectedLine = button.dataset.line;
        if (selectedLine === this.activeLine) return;
        this.setActiveLine(selectedLine);
      });
    });
  }

  setActiveLine(line, options = {}) {
    this.activeLine = line;

    this.lineButtons.forEach((button) => {
      const isActive = button.dataset.line === line;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    this.applyLineFilter(line);

    if (!options.skipReset) {
      this.clearBundle({ skipSave: true });
    }

    this.saveToStorage();
  }

  applyLineFilter(line) {
    const categories = document.querySelectorAll('.bundle-category');
    categories.forEach((category) => {
      const categoryLine = category.dataset.trackLine;
      if (!line || categoryLine === line) {
        category.style.display = '';
      } else {
        category.style.display = 'none';
      }
    });

    if (this.categoryLimits.size) {
      this.evaluateStepProgress();
    }
  }

  setupSearch() {
    const categories = document.querySelectorAll('.bundle-category');

    categories.forEach((category) => {
      const input = category.querySelector('.category-search-input');
      const productsContainer = category.querySelector('.category-products');

      if (!input || !productsContainer) return;

      input.addEventListener('input', () => {
        const query = input.value.trim().toLowerCase();
        const productCards = productsContainer.querySelectorAll('.bundle-product-card');

        productCards.forEach((card) => {
          const titleEl = card.querySelector('.product-title');
          const title = titleEl ? titleEl.textContent.toLowerCase() : '';

          if (!query || title.includes(query)) {
            card.style.display = '';
          } else {
            card.style.display = 'none';
          }
        });
      });
    });
  }

  setupCategoryAccordions() {
    if (!this.categoryElements.length) {
      this.categoryElements = Array.from(document.querySelectorAll('.bundle-category'));
    }

    this.categoryElements.forEach((category) => {
      const toggle = category.querySelector('.category-toggle');
      const products = category.querySelector('.category-products');
      const header = category.querySelector('.category-header');

      if (!toggle || !products) return;

      toggle.addEventListener('click', () => {
        const shouldCollapse = !category.classList.contains('is-collapsed');
        this.setCategoryCollapsed(category, shouldCollapse);
      });

      if (header) {
        header.addEventListener('click', (event) => {
          if (event.target.closest('.category-toggle') || event.target.closest('.category-search-input')) {
            return;
          }
          const shouldCollapse = !category.classList.contains('is-collapsed');
          this.setCategoryCollapsed(category, shouldCollapse);
        });
      }
    });
  }

  setCategoryCollapsed(category, collapsed) {
    if (!category) return;

    const toggle = category.querySelector('.category-toggle');
    const products = category.querySelector('.category-products');
    const nextState = typeof collapsed === 'boolean'
      ? collapsed
      : !category.classList.contains('is-collapsed');

    category.classList.toggle('is-collapsed', nextState);

    if (toggle) {
      toggle.setAttribute('aria-expanded', (!nextState).toString());

      const label = toggle.querySelector('.toggle-label');
      if (label) {
        label.textContent = nextState ? 'Kategorie öffnen' : 'Kategorie schließen';
      }

      const icon = toggle.querySelector('.toggle-icon');
      if (icon) {
        icon.textContent = nextState ? '+' : '−';
      }
    }

    if (products) {
      products.setAttribute('aria-hidden', nextState ? 'true' : 'false');
    }
  }

  getCategoryElementByRole(line, role) {
    if (!line || !role) return null;

    return this.categoryElements.find((category) => {
      return category.dataset.trackLine === line && category.dataset.categoryRole === role;
    }) || null;
  }

  getOrderedCategoriesForLine(line) {
    if (!line) return [];

    const ordered = [];
    this.categorySteps.forEach((role) => {
      const category = this.getCategoryElementByRole(line, role);
      if (category) {
        ordered.push(category);
      }
    });

    return ordered;
  }

  evaluateStepProgress() {
    if (!this.activeLine || !this.categoryLimits.size) {
      return;
    }

    const orderedCategories = this.getOrderedCategoriesForLine(this.activeLine);

    if (!orderedCategories.length) {
      return;
    }

    let currentStepIndex = 0;

    for (let i = 0; i < orderedCategories.length; i++) {
      const category = orderedCategories[i];
      const categoryId = parseInt(category.dataset.categoryId);
      const limit = this.categoryLimits.get(categoryId);
      const threshold = (limit && typeof limit.threshold === 'number') ? limit.threshold : 1;
      const selected = limit ? limit.selected : 0;

      if (threshold <= 0 || selected >= threshold) {
        currentStepIndex = i + 1;
      } else {
        currentStepIndex = i;
        break;
      }
    }

    if (currentStepIndex >= orderedCategories.length) {
      currentStepIndex = orderedCategories.length - 1;
    }

    orderedCategories.forEach((category, index) => {
      this.setCategoryCollapsed(category, index !== currentStepIndex);
    });
  }
  
  handleProductSelection(checkbox) {
  const productCard = checkbox.closest('.bundle-product-card');

  // ✅ kleine Hilfsfunktion, damit "true", " True ", "TRUE" usw. alle funktionieren
  const toBool = (value) => {
    if (value == null) return false;
    return String(value).trim().toLowerCase() === 'true';
  };

  // ✅ Verfügbarkeit robust auslesen
  const isAvailable = toBool(productCard.dataset.available);
  const isInStock = toBool(productCard.dataset.inStock);

  debugLog('🧪 Availability-Check', {
    productId: productCard.dataset.productId,
    availableAttr: productCard.dataset.available,
    inStockAttr: productCard.dataset.inStock,
    isAvailable,
    isInStock
  });

  // Check if product is available
  if (checkbox.checked && (!isAvailable || !isInStock)) {
    debugLog('❌ Produkt nicht verfügbar oder ausverkauft - Auswahl verhindert');
    checkbox.checked = false;

    const status = !isAvailable ? this.translations.status_unavailable : this.translations.status_sold_out;
    const message = this.translations.unavailable.replace('{{ status }}', status);
    this.showNotification(message, 'warning');
    return;
  }
    
    const productId = productCard.dataset.productId;
    const variantId = productCard.dataset.variantId;
    const price = parseInt(checkbox.dataset.price);
    const title = checkbox.dataset.title;
    const categoryIndex = this.getCategoryIndex(productCard);
    
    debugLog(`🔍 Produktauswahl - Kategorie: ${categoryIndex}, Produkt: ${title}`);
    
    if (checkbox.checked) {
      // Check category limits
      const categoryLimit = this.categoryLimits.get(categoryIndex);
      debugLog(`📊 Kategorie ${categoryIndex} - Aktuell: ${categoryLimit?.selected}, Maximum: ${categoryLimit?.max}`);
      
      if (categoryLimit && categoryLimit.selected >= categoryLimit.max) {
        checkbox.checked = false;
        const message = this.translations.category_max.replace('{{ max }}', categoryLimit.max);
        this.showNotification(message, 'warning');
        return;
      }
      
      // Check total product limit
      if (this.selectedProducts.size >= this.maxProducts) {
        checkbox.checked = false;
        const message = this.translations.products_max.replace('{{ max }}', this.maxProducts);
        this.showNotification(message, 'warning');
        return;
      }
      
      // Add product
      this.selectedProducts.set(productId, {
        variantId,
        price,
        title,
        categoryIndex,
        element: productCard
      });
      
      // Update category count
      if (categoryLimit) {
        categoryLimit.selected++;
      }
      
      productCard.classList.add('selected');
      
      // Update button text
      const label = productCard.querySelector('.selection-label');
      if (label) {
        label.textContent = this.translations.deselect;
      }
    } else {
      // Remove product
      const product = this.selectedProducts.get(productId);
      if (product) {
        this.selectedProducts.delete(productId);
        
        // Update category count
        const categoryLimit = this.categoryLimits.get(product.categoryIndex);
        if (categoryLimit) {
          categoryLimit.selected--;
        }
        
        productCard.classList.remove('selected');
        
        // Update button text back to original
        const label = productCard.querySelector('.selection-label');
        if (label) {
          label.textContent = this.translations.select;
        }
      }
    }
    
    this.updateUI();
    this.saveToStorage();
  }
  
  getCategoryIndex(productCard) {
    const category = productCard.closest('.bundle-category');
    const categoryId = category.dataset.categoryId;
    return parseInt(categoryId);
  }
  
  removeProduct(productId) {
    const product = this.selectedProducts.get(productId);
    if (product) {
      // Uncheck the checkbox
      const checkbox = product.element.querySelector('.product-checkbox');
      if (checkbox) {
        checkbox.checked = false;
      }
      
      // Remove from selected products
      this.selectedProducts.delete(productId);
      
      // Update category count
      const categoryLimit = this.categoryLimits.get(product.categoryIndex);
      if (categoryLimit) {
        categoryLimit.selected--;
      }
      
      // Remove visual selection
      product.element.classList.remove('selected');
      
      // Update button text back to original
      const label = product.element.querySelector('.selection-label');
      if (label) {
        label.textContent = this.translations.select;
      }
      
      this.updateUI();
      this.saveToStorage();
    }
  }
  
  clearBundle(options = {}) {
    // Uncheck all checkboxes
    document.querySelectorAll('.product-checkbox:checked').forEach(checkbox => {
      checkbox.checked = false;
    });
    
    // Remove all visual selections and reset button text
    document.querySelectorAll('.bundle-product-card.selected').forEach(card => {
      card.classList.remove('selected');
      const label = card.querySelector('.selection-label');
      if (label) {
        label.textContent = this.translations.select;
      }
    });
    
    // Clear selected products
    this.selectedProducts.clear();
    
    // Reset category counts
    this.categoryLimits.forEach(limit => {
      limit.selected = 0;
    });
    
    this.updateUI();

    if (!options.skipSave) {
      this.saveToStorage();
    }
  }
  
  updateUI() {
    this.updateCategoryCounts();
    this.updateSelectedProductsList();
    this.updatePricing();
    this.updateProgress();
    this.updateAddToCartButton();
    this.evaluateStepProgress();
  }
  
  updateCategoryCounts() {
    document.querySelectorAll('.bundle-category').forEach((category) => {
      const selectedCountEl = category.querySelector('.selected-count');
      const categoryId = parseInt(category.dataset.categoryId);
      const limit = this.categoryLimits.get(categoryId);
      if (selectedCountEl && limit) {
        selectedCountEl.textContent = limit.selected;
      }
    });
  }
  
  updateSelectedProductsList() {
    const listContainer = document.querySelector('.selected-products-list');
    if (!listContainer) return;
    
    if (this.selectedProducts.size === 0) {
      listContainer.innerHTML = `
        <div class="empty-state">
          <p>${this.translations.empty}</p>
        </div>
      `;
      return;
    }
    
    const productsList = Array.from(this.selectedProducts.entries()).map(([productId, product]) => `
      <div class="selected-product-item">
        <div class="selected-product-info">
          <div class="selected-product-name">${product.title}</div>
          <div class="selected-product-price">${this.formatMoney(this.calculateDiscountedPrice(product.price))}<br><span class="bundle-price-note">inkl. ${this.discountPercentage}% Bundle-Rabatt</span></div>
        </div>
        <button type="button" class="remove-product" data-product-id="${productId}">
          ✕
        </button>
      </div>
    `).join('');
    
    listContainer.innerHTML = productsList;
  }
  
    updatePricing() {
    const originalTotal = this.calculateOriginalTotal();      // Summe Originalpreise
    const discountedTotal = this.calculateDiscountedTotal();  // Summe der gerundeten Bundle-Preise
    const discountAmount = originalTotal - discountedTotal;   // Differenz = Rabatt
    const bundleTotal = discountedTotal;                      // Endsumme = Summe der Bundle-Preise

    // Update price displays
    const originalTotalEl = document.querySelector('.original-total-price');
    const discountAmountEl = document.querySelector('.discount-amount-price');
    const bundleTotalEl = document.querySelector('.bundle-total-price');
    const savingsAmountEl = document.querySelector('.savings-amount');
    
    if (originalTotalEl) originalTotalEl.textContent = this.formatMoney(originalTotal);
    if (discountAmountEl) discountAmountEl.textContent = '-' + this.formatMoney(discountAmount);
    if (bundleTotalEl) bundleTotalEl.textContent = this.formatMoney(bundleTotal);
    if (savingsAmountEl) savingsAmountEl.textContent = this.formatMoney(discountAmount);
  }
  
  updateProgress() {
    const selectedCount = this.selectedProducts.size;
    const progressFill = document.querySelector('.progress-fill');
    const selectedCountEl = document.querySelector('.selected-products-count');
    
    if (progressFill) {
      const progressPercentage = Math.min((selectedCount / this.minProducts) * 100, 100);
      progressFill.style.width = `${progressPercentage}%`;
    }
    
    if (selectedCountEl) {
      selectedCountEl.textContent = selectedCount;
    }
  }
  
  updateAddToCartButton() {
    const addToCartButton = document.querySelector('.add-bundle-to-cart');
    if (!addToCartButton) return;
    
    const canAddToCart = this.selectedProducts.size >= this.minProducts;
    addToCartButton.disabled = !canAddToCart;
    
    if (canAddToCart) {
      addToCartButton.textContent = this.translations.add_to_cart;
    } else {
      const remaining = this.minProducts - this.selectedProducts.size;
      const plural = remaining === 1 ? '' : 'e';
      addToCartButton.textContent = this.translations.remaining
        .replace('{{ count }}', remaining)
        .replace('{{ plural }}', plural);
    }
  }
  
  calculateOriginalTotal() {
    return Array.from(this.selectedProducts.values()).reduce((total, product) => total + product.price, 0);
  }
    // NEU: Summe aller rabattierten Einzelpreise
  calculateDiscountedTotal() {
    return Array.from(this.selectedProducts.values())
      .reduce((total, product) => total + this.calculateDiscountedPrice(product.price), 0);
  }

    calculateDiscountAmount(originalTotal) {
    // Rabatt = Original-Summe – Summe der rabattierten (gerundeten) Einzelpreise
    const discountedTotal = this.calculateDiscountedTotal();
    return originalTotal - discountedTotal;
  }
  
  calculateDiscountedPrice(originalPrice) {
  // Rabatt für einen Artikel: ebenfalls zuerst Rabatt berechnen & abrunden
  const discount = Math.floor(originalPrice * (this.discountPercentage / 100));
  return originalPrice - discount;
}
  
  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return this.moneyFormat.replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
  }
  
  async addBundleToCart() {
    if (this.selectedProducts.size < this.minProducts) {
      const message = this.translations.products_min.replace('{{ min }}', this.minProducts);
      this.showNotification(message, 'error');
      return;
    }
    
    const addToCartButton = document.querySelector('.add-bundle-to-cart');
    const originalText = addToCartButton.textContent;
    
    try {
      addToCartButton.textContent = this.translations.adding;
      addToCartButton.disabled = true;
      
      // Generate unique bundle ID
      const bundleId = `BUNDLE-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const productCount = this.selectedProducts.size;
      
            // Calculate total prices
      const originalTotal = this.calculateOriginalTotal();
      const discountedTotal = this.calculateDiscountedTotal();
      const discountAmount = originalTotal - discountedTotal;
      const bundleTotal = discountedTotal;
      
      // Use configured discount code
      const discountCode = this.discountCode;
      
      debugLog('💰 Discount Code:', discountCode);
      
      // Prepare cart items with bundle tracking
      const items = Array.from(this.selectedProducts.values()).map(product => ({
        id: product.variantId,
        quantity: 1,
        properties: {
          '_bundle_id': bundleId,
          '_bundle_count': productCount.toString(),
          '_bundle_discount': this.discountPercentage.toString(),
          '_original_price': product.price.toString(),
          '_discounted_price': this.calculateDiscountedPrice(product.price).toString(),
          '_discount_code': discountCode,
          'inkl.': `${this.discountPercentage}% Bundle-Rabatt`,
        }
      }));
      
      // Get mini-cart section ID for rendering
      const miniCartSection = document.querySelector('cart-drawer[section]');
      const sectionId = miniCartSection ? miniCartSection.getAttribute('section') : 'mini-cart';
      
      debugLog('🛒 Mini-Cart Section ID:', sectionId);
      
      // Add items to cart with sections parameter
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          items,
          sections: sectionId
        })
      });
      
      if (response.ok) {
        const responseData = await response.json();
        debugLog('✅ Bundle zum Warenkorb hinzugefügt:', responseData);
        
        this.showNotification(this.translations.success, 'success');
        
        // Clear the bundle after successful addition
        setTimeout(() => {
          this.clearBundle();
        }, 1000);
        
        // Fetch updated cart
        const cartResponse = await fetch(`${window.themeVariables?.routes?.cartUrl || '/cart'}.js`);
        const cartContent = await cartResponse.json();
        
        debugLog('📦 Aktualisierter Warenkorb:', cartContent);
        debugLog('📦 Cart Item Count:', cartContent.item_count);
        debugLog('🎨 Sections in Response:', responseData.sections ? 'JA' : 'NEIN');
        
        // Trigger cart:updated event first
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: {
            cart: cartContent
          }
        }));
        
        debugLog('✅ cart:updated dispatched');
        
        // Add sections from add response if available
        if (responseData.sections) {
          cartContent.sections = responseData.sections;
          debugLog('✅ Sections hinzugefügt zu cartContent');
        }
        
        // Check cart type setting
        const cartType = window.themeVariables?.settings?.cartType;
        const shouldOpenDrawer = cartType === 'drawer';
        
        debugLog('🛒 Cart Type:', cartType);
        debugLog('🚪 Should Open Drawer:', shouldOpenDrawer);
        
        // Trigger cart:refresh event to update drawer UI
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: cartContent,
            openMiniCart: shouldOpenDrawer
          }
        }));
        
        debugLog('✅ cart:refresh dispatched mit openMiniCart:', shouldOpenDrawer);
        
        // Initialize bundle monitoring
        this.initBundleMonitoring();
        
      } else {
        const errorData = await response.json();
        debugError('❌ Fehler beim Hinzufügen:', errorData);
        throw new Error(errorData.description || 'Failed to add to cart');
      }
    } catch (error) {
      debugError('Error adding bundle to cart:', error);
      this.showNotification(this.translations.error, 'error');
    } finally {
      addToCartButton.textContent = originalText;
      addToCartButton.disabled = this.selectedProducts.size < this.minProducts;
    }
  }
  
  initBundleMonitoring() {
    // Initialize bundle monitoring on page load
    if (typeof window.BundleMonitor === 'undefined') {
      debugLog('⚠️ Bundle Monitor nicht geladen - Bundle-Validierung deaktiviert');
    }
  }
  
  
  showNotification(message, type = 'info', duration = 5000) {
    // Remove existing notification
    const existingNotification = document.querySelector('.bundle-notification');
    if (existingNotification) {
      existingNotification.remove();
    }
    
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `bundle-notification bundle-notification--${type}`;
    notification.innerHTML = `
      <div class="bundle-notification__content">
        <span class="bundle-notification__message">${message}</span>
        <button type="button" class="bundle-notification__close">✕</button>
      </div>
    `;
    
    // Add styles
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#10b981' : type === 'error' ? '#ef4444' : '#3b82f6'};
      color: white;
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 1000;
      max-width: 400px;
      animation: slideInRight 0.3s ease;
    `;
    
    // Add to DOM
    document.body.appendChild(notification);
    
    // Auto remove after specified duration
    setTimeout(() => {
      if (notification.parentNode) {
        notification.style.animation = 'slideOutRight 0.3s ease';
        setTimeout(() => notification.remove(), 300);
      }
    }, duration);
    
    // Manual close
    const closeButton = notification.querySelector('.bundle-notification__close');
    closeButton.addEventListener('click', () => {
      notification.style.animation = 'slideOutRight 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    });
  }
  
  saveToStorage() {
    const bundleData = {
      pageId: this.pageId,
      pageHandle: this.pageHandle,
      selectedProducts: Array.from(this.selectedProducts.entries()),
      categoryLimits: Array.from(this.categoryLimits.entries()),
      activeLine: this.activeLine,
      timestamp: Date.now(),
      version: this.getConfigVersion()
    };
    
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(bundleData));
      debugLog(`💾 Bundle gespeichert unter Key: ${this.storageKey}`);
    } catch (error) {
      debugWarn('Failed to save bundle to localStorage:', error);
    }
  }
  
  loadFromStorage() {
    try {
      const savedData = localStorage.getItem(this.storageKey);
      if (!savedData) {
        debugLog(`📂 Keine gespeicherten Daten für Key: ${this.storageKey}`);
        return;
      }
      
      const bundleData = JSON.parse(savedData);
      
      debugLog(`📂 Lade Bundle-Daten für Key: ${this.storageKey}`, bundleData);
      
      // Check if data is for the correct page
      if (bundleData.pageId && bundleData.pageId !== this.pageId) {
        debugLog(`⚠️ Page ID stimmt nicht überein. Gespeichert: ${bundleData.pageId}, Aktuell: ${this.pageId}`);
        localStorage.removeItem(this.storageKey);
        return;
      }
      
      // Check if data is not too old (24 hours)
      if (Date.now() - bundleData.timestamp > 24 * 60 * 60 * 1000) {
        debugLog('⏰ Gespeicherte Daten sind älter als 24 Stunden');
        localStorage.removeItem(this.storageKey);
        return;
      }
      
      // Check if configuration has changed
      if (bundleData.version !== this.getConfigVersion()) {
        debugLog('🔄 Konfiguration hat sich geändert');
        localStorage.removeItem(this.storageKey);
        return;
      }
      
      if (bundleData.activeLine && this.lineButtons.length) {
        this.setActiveLine(bundleData.activeLine, { skipReset: true });
      }

      // Restore selected products
      if (bundleData.selectedProducts) {
        bundleData.selectedProducts.forEach(([productId, productData]) => {
          const productCard = document.querySelector(`[data-product-id="${productId}"]`);
          if (productCard) {
            const checkbox = productCard.querySelector('.product-checkbox');
            if (checkbox) {
              checkbox.checked = true;
              productCard.classList.add('selected');
              
              // Update button text
              const label = productCard.querySelector('.selection-label');
              if (label) {
                label.textContent = this.translations.deselect;
              }
              
              this.selectedProducts.set(productId, {
                ...productData,
                element: productCard
              });
            }
          }
        });
      }
      
      // Restore category limits (only the selected count, not the max values)
      if (bundleData.categoryLimits) {
        bundleData.categoryLimits.forEach(([categoryId, savedLimit]) => {
          const currentLimit = this.categoryLimits.get(categoryId);
          if (currentLimit) {
            // Keep the current max value, but restore the selected count
            currentLimit.selected = savedLimit.selected || 0;
          }
        });
      }
      
      this.updateUI();
    } catch (error) {
      debugWarn('Failed to load bundle from localStorage:', error);
      localStorage.removeItem(this.storageKey);
    }
  }
  
  getConfigVersion() {
    // Create a simple hash based on category configuration
    const categories = document.querySelectorAll('.bundle-category');
    let configString = '';
    
    categories.forEach((category, index) => {
      const maxSelection = category.dataset.maxSelection;
      const productCount = category.querySelectorAll('.bundle-product-card').length;
      configString += `${index}-${maxSelection}-${productCount}`;
    });
    
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < configString.length; i++) {
      const char = configString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return hash.toString();
  }
}

// Add CSS animations
const style = document.createElement('style');
style.textContent = `
  @keyframes slideInRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes slideOutRight {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }
  
  .bundle-notification__content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  
  .bundle-notification__close {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    font-size: 18px;
    padding: 0;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .bundle-notification__close:hover {
    opacity: 0.8;
  }
`;
document.head.appendChild(style);

// Note: BundleBuilder is initialized from the bundle-builder.liquid section
// with proper configuration parameters. No automatic initialization here.

// Export for use in other scripts
window.BundleBuilder = BundleBuilder;
